import { useState } from "react";
import { Form, Input, Button, message } from "antd";
import {
  ArrowLeftOutlined,
  LockOutlined,
  MailOutlined,
} from "@ant-design/icons";
import userServices from "@/services/auth.service";

// Step 1: enter email -> POST checkuserexist (must exist).
// Step 2: enter new password + confirm -> POST forgetpassword.
export default function ForgotPassword({ onBack }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCheckEmail = async (values) => {
    const value = values.email.trim();
    setLoading(true);
    try {
      const res = await userServices.checkUserExist({ email: value });
      if (res?.success) {
        setEmail(value);
        setStep(2);
      } else {
        message.error("No account found with this email");
      }
    } catch (err) {
      // The API answers 400 { success: false } when the email is not registered.
      if (err?.response?.status === 400) {
        message.error("No account found with this email");
      } else {
        message.error(err?.response?.data?.message || "Server not reachable");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (values) => {
    setLoading(true);
    try {
      await userServices.forgetPassword({
        email,
        password: values.password,
        confirmpassword: values.confirmpassword,
      });
      message.success("Password updated successfully. Please sign in.");
      onBack();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          (err?.response
            ? "Could not update the password"
            : "Server not reachable"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={step === 2 ? () => setStep(1) : onBack}
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 transition hover:text-[#1858EF]"
      >
        <ArrowLeftOutlined /> {step === 2 ? "Change email" : "Back to sign in"}
      </button>

      {step === 1 ? (
        <Form
          layout="vertical"
          requiredMark={false}
          onFinish={handleCheckEmail}
          disabled={loading}
        >
          <p className="mb-4 text-sm text-gray-500">
            Enter your registered email to reset your password.
          </p>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: "Please input your email!" },
              { type: "email", message: "Enter a valid email address" },
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-gray-400" />}
              placeholder="Email"
              autoComplete="email"
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>
            Continue
          </Button>
        </Form>
      ) : (
        <Form
          layout="vertical"
          requiredMark={false}
          onFinish={handleReset}
          disabled={loading}
        >
          <p className="mb-4 text-sm text-gray-500">
            Set a new password for{" "}
            <span className="font-medium text-gray-800">{email}</span>.
          </p>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: "Please input a new password!" },
              { min: 8, message: "At least 8 characters" },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                message: "Use upper and lower case letters and a number",
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-gray-400" />}
              placeholder="New password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirmpassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password!" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return !value || getFieldValue("password") === value
                    ? Promise.resolve()
                    : Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-gray-400" />}
              placeholder="Confirm password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Button type="primary" htmlType="submit" block loading={loading}>
            Change Password
          </Button>
        </Form>
      )}
    </div>
  );
}
